import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Copy, MessageCircleOff, ScanSearch, ShieldCheck, UserRound } from 'lucide-react';
import { useQuality } from '@/hooks/useInsights';
import type { QualityResponse } from '@/api/insights';
import { insightsInvitesHref, pctText, shareOf } from '@/lib/insights';
import { cn } from '@/lib/utils';
import { useInsightsScope } from './scope';
import {
  BarList,
  KpiTile,
  LoadError,
  MetricLabel,
  Loading,
  Note,
  Panel,
  RateValue,
  Section,
  SortHeader,
  Unavailable,
  useSort,
  useSorted,
} from './parts';

type SortKey = 'name' | 'reviewed' | 'ok' | 'fail' | 'unclear' | 'failRate';

/** Below this review coverage, outcomes are flagged as a thin sample. */
const LOW_COVERAGE = 0.2;

const FALLBACK_DEFS: Record<string, string> = {
  liveUnreviewed: 'Proof screenshots on invites still in play that nobody has reviewed yet.',
  proofReviewed: 'Proof screenshots given a verdict in this period.',
  coverage: 'Reviewed ÷ proofs that could have been reviewed.',
  ok: 'Reviewed and approved.',
  fail: 'Reviewed and rejected.',
  unclear: 'Reviewed but the screenshot didn’t settle it.',
  proofFailRate: 'Rejected ÷ reviewed.',
  duplicateCreatorsLive: 'Creators on more than one live invite (same channel and handle), right now.',
  duplicateCreators: 'Creators on more than one valid invite (same channel and handle), across all time.',
  selfInvites: 'Invites whose creator is the outreacher’s own clipping account.',
  backdatedConversions: 'Invites recorded as posted before they were logged.',
  invitesWithoutMessage: 'Replies logged with no “I messaged them” record before them.',
};

export function QualityTab(): JSX.Element {
  const scope = useInsightsScope();
  const q = useQuality(scope.params);
  const [sort, toggleSort] = useSort<SortKey>({ key: 'failRate', dir: 'desc' });
  const rows = useSorted(q.data?.byOutreacher ?? [], sort, (r: QualityResponse['byOutreacher'][number], k) =>
    k === 'name' ? r.name.toLowerCase() : r[k],
    (r, k) => (k === 'failRate' ? r.reviewed : undefined),
  );

  if (q.isLoading) return <Loading label="Loading quality checks…" />;
  if (q.isError || !q.data) return <LoadError error={q.error} what="quality checks" />;

  const d = q.data;
  const def = (key: string): string | undefined => d.definitions?.[key] ?? FALLBACK_DEFS[key];
  const p = d.proof;
  const reviewed = p.ok + p.fail + p.unclear;
  const window = scope.windows.current;
  const team = scope.team || undefined;

  return (
    <div className="space-y-6">
      <Section
        title={`Proof review backlog: ${p.liveUnreviewed.toLocaleString()} unreviewed`}
        description="Proofs on invites still in play that nobody has looked at, and what was reviewed in this period."
      >
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <KpiTile
            icon={ScanSearch}
            label="Unreviewed now"
            def={def('liveUnreviewed')}
            value={p.liveUnreviewed.toLocaleString()}
            to="/admin/screenshot-audit"
            sub="open Proof review to work through them"
          />
          <KpiTile
            icon={ShieldCheck}
            label="Reviewed in period"
            def={def('proofReviewed')}
            value={p.reviewedInWindow.toLocaleString()}
          />
          <KpiTile
            label="Coverage"
            def={def('coverage')}
            value={<RateValue rate={p.coverage} reason="No proofs to review in this period" />}
            sub="of proofs that could be reviewed"
          />
        </div>
        {p.coverage != null && p.coverage < LOW_COVERAGE ? (
          <Note tone="warning">
            Only {pctText(p.coverage)} of proofs have been reviewed, so the outcomes and fail rates below describe a
            small sample that may not be typical.
          </Note>
        ) : null}
        <Panel>
          {reviewed === 0 ? (
            <p className="text-sm text-muted-foreground">No proofs were reviewed in this period.</p>
          ) : (
            <BarList
              ariaLabel="Review outcomes"
              rows={[
                { key: 'ok', label: 'Approved', value: p.ok, display: p.ok.toLocaleString(), sub: pctText(shareOf(p.ok, reviewed)) },
                {
                  key: 'fail',
                  label: 'Rejected',
                  value: p.fail,
                  display: p.fail.toLocaleString(),
                  sub: pctText(shareOf(p.fail, reviewed)),
                  tone: 'warning',
                },
                {
                  key: 'unclear',
                  label: 'Unclear',
                  value: p.unclear,
                  display: p.unclear.toLocaleString(),
                  sub: pctText(shareOf(p.unclear, reviewed)),
                  tone: 'muted',
                },
              ]}
            />
          )}
        </Panel>
      </Section>

      <Section
        title="Whose proofs fail review?"
        description="Proofs reviewed in this period, by outreacher. A greyed fail rate rests on fewer than 10 reviews."
      >
        {rows.length === 0 ? (
          <Panel>
            <p className="text-sm text-muted-foreground">No proofs were reviewed in this period.</p>
          </Panel>
        ) : (
          <div className="table-frame relative overflow-x-auto">
            <table className="responsive-stack w-full whitespace-nowrap text-sm" aria-label="Proof review by outreacher">
              <thead>
                <tr className="text-left">
                  <SortHeader label="Outreacher" k="name" sort={sort} onSort={toggleSort} numeric={false} />
                  <SortHeader label="Reviewed" k="reviewed" sort={sort} onSort={toggleSort} def={def('proofReviewed')} />
                  <SortHeader label="Approved" k="ok" sort={sort} onSort={toggleSort} def={def('ok')} />
                  <SortHeader label="Rejected" k="fail" sort={sort} onSort={toggleSort} def={def('fail')} />
                  <SortHeader label="Unclear" k="unclear" sort={sort} onSort={toggleSort} def={def('unclear')} />
                  <SortHeader label="Fail rate" k="failRate" sort={sort} onSort={toggleSort} def={def('proofFailRate')} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.inviterId} className="border-t border-border transition-colors hover:bg-accent/40">
                    <td className="px-3 py-2 font-medium">
                      <Link
                        to={insightsInvitesHref({ team, inviterId: r.inviterId, window })}
                        className="hover:underline"
                        title="Open their invites logged in this period"
                      >
                        {r.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right font-mono" data-label="Reviewed">
                      {r.reviewed.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono" data-label="Approved">
                      {r.ok.toLocaleString()}
                    </td>
                    <td className={cn('px-3 py-2 text-right font-mono', r.fail > 0 && 'text-pending-strong')} data-label="Rejected">
                      {r.fail.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono" data-label="Unclear">
                      {r.unclear.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right" data-label="Fail rate">
                      <RateValue rate={r.failRate} n={r.reviewed} of="reviews" reason="Nothing reviewed" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="What looks off?" description="Patterns worth a look. None is proof of a problem on its own.">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiTile
            icon={Copy}
            label="Duplicate creators (live)"
            def={def('duplicateCreatorsLive')}
            value={d.signals.duplicateCreatorsLive.toLocaleString()}
            sub={
              <MetricLabel
                label={`${d.signals.duplicateCreators.toLocaleString()} across all time`}
                def={def('duplicateCreators')}
              />
            }
          />
          <KpiTile
            icon={CalendarClock}
            label="Posted before logged"
            def={def('backdatedConversions')}
            value={d.signals.backdatedConversions.toLocaleString()}
          />
          <KpiTile
            icon={MessageCircleOff}
            label="Replies without a message"
            def={def('invitesWithoutMessage')}
            value={d.signals.invitesWithoutMessage.toLocaleString()}
            sub="logged with no “I messaged them” first"
          />
          <KpiTile
            icon={UserRound}
            label="Self-invites"
            def={def('selfInvites')}
            value={
              d.signals.selfInvites == null ? (
                <Unavailable reason={def('selfInvites') ?? 'Not measurable: outreachers’ own handles aren’t recorded'} />
              ) : (
                d.signals.selfInvites.toLocaleString()
              )
            }
            sub={d.signals.selfInvites == null ? 'not measurable yet' : undefined}
          />
        </div>
        {d.signals.duplicateCreatorsLive > 0 ? (
          <Note>Duplicates also show per outreacher on the Outreachers tab.</Note>
        ) : null}
      </Section>
    </div>
  );
}
